import type {
  MonsterExperienceRewardDefinition,
  MonsterLootTable,
  MonsterRyoRewardDefinition,
} from "@ninja/domain";
import { isMonsterRewardDefinition } from "@ninja/domain";
import {
  RAW_MONSTER_CONTENT,
  type MonsterContentId,
} from "./registry.generated.js";
import { parseMonsterLootTable } from "./loot.js";
import {
  isBossEncounterDefinition,
  type BossEncounterDefinition,
} from "./BossEncounterDefinition.js";
import {
  parseMonsterMovement,
  type MonsterMovementDefinition,
} from "./movement.js";

export interface MonsterStatCurve {
  base: number;
  perLevel: number;
}

export interface MonsterContentStats {
  maxHp: MonsterStatCurve;
  physicalAttack: MonsterStatCurve;
  techniqueAttack: MonsterStatCurve;
  physicalDefense: MonsterStatCurve;
  techniqueDefense: MonsterStatCurve;
  precision: MonsterStatCurve;
  evasion: MonsterStatCurve;
  critical: MonsterStatCurve;
  criticalResistance: MonsterStatCurve;
  physicalPenetration: MonsterStatCurve;
  techniquePenetration: MonsterStatCurve;
  tenacity: MonsterStatCurve;
  controlPower: MonsterStatCurve;
}

export interface MonsterHardControlProfile {
  immune?: boolean;
  durationMultiplier?: number;
}

export interface MonsterContentDefinition {
  schemaVersion: 1;
  id: string;
  label: string;
  category: "normal" | "elite" | "boss";
  faction: string;
  behaviorProfile: string;
  bossEncounter?: BossEncounterDefinition;
  hardControl?: MonsterHardControlProfile;
  designReference: string;
  availability: "active" | "preview-only";
  aliases: string[];
  visualAppearanceId: number;
  levelProgression: {
    referenceLevel: number;
    minLevel?: number;
    maxLevel?: number;
  };
  stats: MonsterContentStats;
  movement: MonsterMovementDefinition;
  basicAttacks: Array<{
    id: string;
    range: number;
    intervalMs: number;
    damageType: "physical" | "technique";
    attribute: "physicalAttack" | "techniqueAttack";
    coefficient: number;
  }>;
  respawnMs: number | null;
  rewards: {
    experience: MonsterExperienceRewardDefinition;
    ryo: MonsterRyoRewardDefinition;
  };
  abilities: Array<{
    id: string;
    label: string;
    runtimeImplemented: boolean;
  }>;
  loot?: MonsterLootTable;
}

export interface MonsterCalculatedStats {
  maxHp: number;
  physicalAttack: number;
  techniqueAttack: number;
  physicalDefense: number;
  techniqueDefense: number;
  precision: number;
  evasion: number;
  critical: number;
  criticalResistance: number;
  physicalPenetration: number;
  techniquePenetration: number;
  tenacity: number;
  controlPower: number;
}

const statNames: Array<keyof MonsterContentStats> = [
  "maxHp",
  "physicalAttack",
  "techniqueAttack",
  "physicalDefense",
  "techniqueDefense",
  "precision",
  "evasion",
  "critical",
  "criticalResistance",
  "physicalPenetration",
  "techniquePenetration",
  "tenacity",
  "controlPower",
];

function loadMonsterDefinition(value: unknown): MonsterContentDefinition {
  if (!value || typeof value !== "object")
    throw new TypeError("Monster content must be a JSON object");
  const definition = value as MonsterContentDefinition;
  if (
    !definition.id ||
    definition.schemaVersion !== 1 ||
    !definition.label ||
    !["normal", "elite", "boss"].includes(definition.category) ||
    !["active", "preview-only"].includes(definition.availability) ||
    !Number.isInteger(definition.visualAppearanceId) ||
    !definition.levelProgression ||
    !Number.isInteger(definition.levelProgression.referenceLevel) ||
    !definition.rewards ||
    !isMonsterRewardDefinition(definition.rewards.experience) ||
    !isMonsterRewardDefinition(definition.rewards.ryo) ||
    !Array.isArray(definition.basicAttacks) ||
    !Array.isArray(definition.abilities)
  )
    throw new TypeError(
      "Monster content has invalid identity or runtime fields",
    );
  const movement = parseMonsterMovement(definition.movement, definition.id);
  if (
    definition.bossEncounter !== undefined &&
    (definition.category !== "boss" ||
      !isBossEncounterDefinition(definition.bossEncounter))
  )
    throw new TypeError(`${definition.id} has an invalid boss encounter profile`);
  if (
    definition.hardControl !== undefined &&
    !isMonsterHardControlProfile(definition.hardControl)
  )
    throw new TypeError(`${definition.id} has an invalid hard-control profile`);
  for (const statName of statNames) {
    const curve = definition.stats?.[statName];
    if (
      !curve ||
      !Number.isFinite(curve.base) ||
      !Number.isFinite(curve.perLevel)
    )
      throw new TypeError(`${definition.id} has an invalid ${statName} curve`);
  }
  if (
    definition.basicAttacks.some(
      (attack) =>
        !attack.id ||
        !Number.isFinite(attack.range) ||
        !Number.isFinite(attack.intervalMs) ||
        !Number.isFinite(attack.coefficient),
    )
  )
    throw new TypeError(`${definition.id} has an invalid basic attack`);
  const rawLoot = (value as { loot?: unknown }).loot;
  return rawLoot === undefined
    ? { ...definition, movement }
    : {
        ...definition,
        movement,
        loot: parseMonsterLootTable(rawLoot, definition.id),
      };
}

function isMonsterHardControlProfile(
  value: unknown,
): value is MonsterHardControlProfile {
  if (!value || typeof value !== "object") return false;
  const profile = value as MonsterHardControlProfile;
  const fields = Object.keys(profile);
  return (
    fields.length > 0 &&
    fields.every((field) => field === "immune" || field === "durationMultiplier") &&
    (profile.immune === undefined || typeof profile.immune === "boolean") &&
    (profile.durationMultiplier === undefined ||
      (Number.isFinite(profile.durationMultiplier) &&
        profile.durationMultiplier > 0 &&
        profile.durationMultiplier <= 1)) &&
    !(profile.immune === true && profile.durationMultiplier !== undefined)
  );
}

export type { MonsterContentId } from "./registry.generated.js";

export const MONSTER_CONTENT: Readonly<
  Record<MonsterContentId, MonsterContentDefinition>
> = Object.fromEntries(
  Object.entries(RAW_MONSTER_CONTENT).map(([id, definition]) => [
    id,
    loadMonsterDefinition(definition),
  ]),
) as Record<MonsterContentId, MonsterContentDefinition>;

for (const [id, definition] of Object.entries(MONSTER_CONTENT))
  if (id !== definition.id)
    throw new Error(
      `Monster content key ${id} does not match ${definition.id}`,
    );

for (const [id, definition] of Object.entries(MONSTER_CONTENT)) {
  if (!definition.bossEncounter) continue;
  const summons = definition.bossEncounter.summons;
  const monsterIds =
    summons.groups?.map(({ monsterId }) => monsterId) ??
    (summons.monsterId ? [summons.monsterId] : []);
  for (const monsterId of monsterIds)
    if (!Object.hasOwn(MONSTER_CONTENT, monsterId))
      throw new Error(`${id} boss encounter references unknown summon ${monsterId}`);
}

export function monsterStatsForContent(
  monsterId: MonsterContentId,
  level: number,
): MonsterCalculatedStats {
  const definition = MONSTER_CONTENT[monsterId];
  if (!Number.isInteger(level) || level < 1)
    throw new RangeError("monster level must be positive");
  if (
    (definition.levelProgression.minLevel !== undefined &&
      level < definition.levelProgression.minLevel) ||
    (definition.levelProgression.maxLevel !== undefined &&
      level > definition.levelProgression.maxLevel)
  )
    throw new RangeError(`${monsterId} does not support level ${level}`);

  const delta = level - definition.levelProgression.referenceLevel;
  return Object.fromEntries(
    Object.entries(definition.stats).map(([key, curve]) => [
      key,
      curve.base + curve.perLevel * delta,
    ]),
  ) as MonsterCalculatedStats;
}
